import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { statusLabel } from "@/lib/status";
import { ProblemMoveDrawer } from "@/components/ProblemMoveDrawer";
import { Truck, Layers, PackageCheck, PackagePlus, PackageMinus, Move, ShoppingCart, Tags, Undo2, ScanLine, Search, ArrowRight, DollarSign, Activity, Inbox, ShieldAlert, ChevronRight, ChevronDown, Check, AlertTriangle, X, FlaskConical, ShieldCheck, Camera, Images, ListChecks, Boxes, MapPin, RotateCcw, ArrowRightLeft, Package, TrendingUp, FileSpreadsheet, Users, SlidersHorizontal, DatabaseBackup, ScrollText, GitCompareArrows, MailWarning } from "lucide-react";

const QH_ICONS = {
  ScanLine, PackagePlus, PackageCheck, Move, Search, Layers, Truck,
  ShoppingCart, PackageMinus, Tags, Undo2, Inbox,
  Camera, Images, ListChecks, Boxes, MapPin, RotateCcw, ArrowRightLeft,
  Package, TrendingUp, FileSpreadsheet, Users, SlidersHorizontal, DatabaseBackup, ScrollText, GitCompareArrows,
};

const TX_LABEL = {
  "donor.created": "Truck added", "part.created": "Part captured", "part.finalized": "Part set Available",
  "part.putaway": "Part put away", "part.split": "Assembly split", "tag.reprinted": "Tag reprinted",
  "part.status_change": "Status changed", "donor.status_change": "Truck status changed", "part.identify": "Part identified",
};
const txLabel = (t) => TX_LABEL[t] || (t || "").replace(/[._]/g, " ");

// Big, mobile-first quick-hit tile. The Scan/Lookup tile is styled as the prominent
// red primary; everything else is a clean white touch target.
function QuickHit({ item, onClick }) {
  const Icon = QH_ICONS[item.icon] || ScanLine;
  const scan = item.key === "scan_lookup";
  return (
    <button data-testid={`qh-${item.key}`} onClick={onClick}
      className={`group text-left rounded-2xl border p-5 min-h-[104px] flex flex-col justify-between transition-all active:scale-[0.98] ${scan ? "col-span-2 border-[#B01E28] bg-[#B01E28] shadow-md" : "border-slate-200 bg-white shadow-sm hover:border-[#E0781A] hover:shadow"}`}>
      <div className="flex items-center justify-between">
        <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${scan ? "bg-white/15" : "bg-red-50"}`}>
          <Icon className={`w-6 h-6 ${scan ? "text-white" : "text-[#B01E28]"}`} strokeWidth={2.2} />
        </div>
        <ArrowRight className={`w-5 h-5 ${scan ? "text-white/80" : "text-slate-200 group-hover:text-[#B01E28]"}`} />
      </div>
      <div className={`mt-3 font-display font-black leading-tight ${scan ? "text-white text-xl" : "text-[#1F1F1F] text-base"}`}>{item.label}</div>
    </button>
  );
}

function Queue({ testid, label, value, color, onClick }) {
  return (
    <button data-testid={testid} onClick={onClick} className="text-left border border-slate-200 bg-white rounded-xl shadow-sm p-4 hover:border-[#E0781A] hover:shadow transition-all">
      <div className={`font-display text-3xl font-black ${color || "text-[#1F1F1F]"}`}>{value}</div>
      <div className="font-mono text-[10px] uppercase tracking-wider text-slate-500 mt-1 flex items-center gap-1">{label} <ArrowRight className="w-3 h-3 opacity-40" /></div>
    </button>
  );
}

const KEY_ICON = {
  needs_id: Tags, needs_grade: ListChecks, needs_price: DollarSign, needs_putaway: MapPin, no_home: MapPin,
  pending_completion: PackageCheck, photo_queue: Camera, review_queue: ShieldCheck, pick_queue: ShoppingCart,
  pricing_queue: DollarSign, finalize_ready: PackageCheck, transfers_receipt: ArrowRightLeft,
  problem_moves: ShieldAlert, sell_candidates: TrendingUp, recommended_to_sell: TrendingUp, my_requests: Inbox, team_requests: Inbox,
  security_review: ShieldAlert, backup: DatabaseBackup, sample_mixed: FlaskConical,
};
const BAND_LABEL = { work: "My work — to complete", attention: "Needs attention", alert: "Alerts" };

// One big, color-coded task card. Most cards are a visibility layer — tap jumps straight to
// the target screen. Expandable cards (problem moves, transfers awaiting receipt) open an
// INLINE panel on Home instead of jumping (H2 foreman verify).
function TaskCard({ g, onClick, expandable, expanded }) {
  const Icon = KEY_ICON[g.key] || ListChecks;
  const zero = !g.count;
  const accent = zero ? "text-emerald-600" : g.severity === "high" ? "text-[#B01E28]" : g.band === "alert" ? "text-slate-700" : "text-[#E0781A]";
  const preview = (g.items || []).map((i) => i.short_code || i.cart_number || i.transfer_number || i.stock_number || i.reason || i.name).filter(Boolean).slice(0, 3).join(", ");
  return (
    <button data-testid={`task-${g.key}`} onClick={onClick} aria-expanded={expandable ? expanded : undefined}
      className={`text-left rounded-2xl border bg-white shadow-sm p-4 min-h-[96px] flex flex-col justify-between hover:shadow hover:border-[#E0781A] transition-all active:scale-[0.98] ${expanded ? "ring-2 ring-[#E0781A] border-[#E0781A]" : !zero && g.severity === "high" ? "border-[#B01E28]/40" : "border-slate-200"}`}>
      <div className="flex items-center justify-between">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${zero ? "bg-emerald-50" : g.severity === "high" ? "bg-red-50" : "bg-orange-50"}`}><Icon className={`w-5 h-5 ${accent}`} strokeWidth={2.2} /></div>
        <span className={`font-display text-2xl font-black ${accent}`}>{g.count}</span>
      </div>
      <div className="mt-2">
        <div className="text-sm font-semibold text-[#1F1F1F] leading-tight">{g.label}</div>
        {expandable
          ? <div className="text-[10px] font-bold uppercase tracking-wide text-[#E0781A] mt-0.5 flex items-center gap-0.5">{expanded ? "Close" : "Tap to verify"}<ChevronDown className={`w-3 h-3 transition-transform ${expanded ? "rotate-180" : ""}`} /></div>
          : preview && <div className="text-[10px] font-mono text-slate-400 truncate mt-0.5">{preview}</div>}
      </div>
    </button>
  );
}

// INLINE problem-move resolve. A problem move is a DECISION (acknowledge a questionable move),
// nothing physical to verify — so it's safe to resolve in-place. Undo-toast pattern: tap Resolve
// commits after a short window; a "Undo" toast lets a mis-tap reverse instantly (no API write fires
// until the window closes). Reuses POST /problem-moves/{id}/resolve (perm inherited server-side).
function ProblemMovesPanel({ onChanged }) {
  const [items, setItems] = useState(null);
  const [notes, setNotes] = useState({});
  const [pending, setPending] = useState([]);
  const [detailId, setDetailId] = useState(null);
  const timers = useRef({});

  const load = () => api.get("/problem-moves").then(({ data }) => setItems(data.items || [])).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  useEffect(() => () => { Object.values(timers.current).forEach(clearTimeout); }, []);

  const onResolved = () => { load(); window.dispatchEvent(new Event("problem-moves-changed")); onChanged?.(); };

  const undo = (id) => {
    if (timers.current[id]) { clearTimeout(timers.current[id]); delete timers.current[id]; }
    setPending((p) => p.filter((x) => x !== id));
  };
  const commit = (id) => {
    const note = (notes[id] || "").trim();
    setPending((p) => [...p, id]);
    timers.current[id] = setTimeout(async () => {
      try {
        await api.post(`/problem-moves/${id}/resolve`, { note: note || undefined });
        setItems((xs) => (xs || []).filter((x) => x.id !== id));
        window.dispatchEvent(new Event("problem-moves-changed"));
        onChanged?.();
      } catch { setPending((p) => p.filter((x) => x !== id)); toast.error("Couldn't resolve — try again."); }
      delete timers.current[id];
    }, 5000);
    toast("Problem move resolved", { description: "Undo within 5s", duration: 5000,
      action: { label: "Undo", onClick: () => undo(id) } });
  };

  if (items === null) return <div data-testid="problem-moves-panel" className="rounded-xl border border-amber-200 bg-amber-50/60 p-4 text-sm text-slate-400 font-mono">Loading problem moves…</div>;
  if (!items.length) return <div data-testid="problem-moves-panel" className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-4 text-sm text-emerald-700">All clear — no unresolved problem moves.</div>;
  return (
    <div data-testid="problem-moves-panel" className="rounded-xl border-2 border-amber-300 bg-amber-50 overflow-hidden">
      <div className="px-3 py-2 bg-amber-100/70 flex items-center gap-2">
        <AlertTriangle className="w-4 h-4 text-amber-700" />
        <span className="font-mono text-[11px] uppercase tracking-widest text-amber-800 font-semibold">Verify the tote's real location, then resolve</span>
      </div>
      <div className="divide-y divide-amber-100 max-h-80 overflow-y-auto">
        {items.map((p) => {
          const isPending = pending.includes(p.id);
          return (
            <div key={p.id} data-testid={`home-problem-move-${p.id}`} className={`px-3 py-2.5 text-sm ${isPending ? "opacity-60" : ""}`}>
              {isPending ? (
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-emerald-600 shrink-0" />
                  <span className="flex-1 text-emerald-700 font-medium">Resolved</span>
                  <button data-testid={`home-undo-problem-${p.id}`} onClick={() => undo(p.id)}
                    className="rounded-lg h-8 px-3 border border-amber-300 text-amber-800 text-xs font-bold hover:bg-amber-100 flex items-center gap-1"><Undo2 className="w-3.5 h-3.5" />Undo</button>
                </div>
              ) : (
                <div className="flex items-start gap-2">
                  <span className={`rounded-lg text-[9px] uppercase font-mono px-1.5 py-0.5 shrink-0 mt-0.5 ${p.kind === "zero_parts" ? "bg-amber-200 text-amber-900" : "bg-red-200 text-red-900"}`}>{p.kind === "zero_parts" ? "0 parts" : "error"}</span>
                  <div className="flex-1 min-w-0">
                    <div className="font-mono text-xs">{p.container_code || p.container_scan || "?"}{p.dest_code && <span> → {p.dest_code}</span>}</div>
                    <div className="text-slate-500 text-[11px] truncate" title={p.reason}>{p.reason}</div>
                    <div className="text-slate-400 text-[10px] font-mono truncate">{p.by} · {p.at ? new Date(p.at).toLocaleString() : ""}</div>
                    <input data-testid={`home-problem-note-${p.id}`} value={notes[p.id] || ""} onChange={(e) => setNotes((n) => ({ ...n, [p.id]: e.target.value }))}
                      placeholder="Note (optional)" className="mt-1.5 w-full rounded-lg border border-amber-200 bg-white px-2 py-1 text-xs focus:outline-none focus:border-amber-400" />
                  </div>
                  <div className="flex flex-col gap-1.5 shrink-0">
                    <button data-testid={`home-problem-details-${p.id}`} onClick={() => setDetailId(p.id)}
                      className="rounded-lg h-8 px-3 bg-[#1F1F1F] text-white text-xs font-bold hover:bg-black flex items-center gap-1 active:scale-95 transition-transform"><ArrowRightLeft className="w-3.5 h-3.5" />Details / Fix</button>
                    <button data-testid={`home-resolve-problem-${p.id}`} onClick={() => commit(p.id)}
                      className="rounded-lg h-8 px-3 bg-amber-600 text-white text-xs font-bold hover:bg-amber-700 flex items-center gap-1 active:scale-95 transition-transform"><Check className="w-3.5 h-3.5" />Resolve</button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      <ProblemMoveDrawer id={detailId} open={!!detailId} onClose={() => setDetailId(null)} onResolved={onResolved} />
    </div>
  );
}

// INLINE transfers-awaiting-receipt. Receiving requires SCANNING the destination bin + item (that
// scan is the physical proof the part arrived) — so this panel only SHOWS the in-transit transfers;
// each "Receive" JUMPS to the scan screen. Never let inline convenience bypass a physical guardrail.
function TransfersReceiptPanel({ onReceive }) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    api.get("/transfers").then(({ data }) => setItems((data || []).filter((t) => ["in_transit", "partially_received"].includes(t.status)))).catch(() => setItems([]));
  }, []);
  if (items === null) return <div data-testid="transfers-receipt-panel" className="rounded-xl border border-indigo-200 bg-indigo-50/60 p-4 text-sm text-slate-400 font-mono">Loading transfers…</div>;
  if (!items.length) return <div data-testid="transfers-receipt-panel" className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-4 text-sm text-emerald-700">Nothing in transit right now.</div>;
  return (
    <div data-testid="transfers-receipt-panel" className="rounded-xl border-2 border-indigo-200 bg-indigo-50/40 overflow-hidden">
      <div className="px-3 py-2 bg-indigo-100/70 flex items-center gap-2">
        <ArrowRightLeft className="w-4 h-4 text-indigo-700" />
        <span className="font-mono text-[11px] uppercase tracking-widest text-indigo-800 font-semibold">Scan the item + bin to receive — proof it physically arrived</span>
      </div>
      <div className="divide-y divide-indigo-100 max-h-80 overflow-y-auto">
        {items.map((t) => (
          <div key={t.id} data-testid={`home-transfer-${t.id}`} className="flex items-center gap-2 px-3 py-2.5 text-sm">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <span className="font-mono text-xs font-bold text-slate-700">{t.transfer_number}</span>
                <span className={`rounded text-[9px] uppercase font-mono px-1.5 py-0.5 ${t.status === "partially_received" ? "bg-amber-100 text-amber-700" : "bg-indigo-100 text-indigo-700"}`}>{(t.status || "").replace("_", " ")}</span>
              </div>
              {t.name && <div className="text-slate-500 text-[11px] truncate">{t.name}</div>}
              <div className="text-slate-400 text-[10px] font-mono truncate">{t.from_site_code} → {t.to_site_code} · {t.received_count}/{t.item_count} received</div>
            </div>
            <button data-testid={`home-receive-transfer-${t.id}`} onClick={onReceive}
              className="rounded-lg h-8 px-3 bg-indigo-600 text-white text-xs font-bold hover:bg-indigo-700 shrink-0 flex items-center gap-1 active:scale-95 transition-transform"><ScanLine className="w-3.5 h-3.5" />Receive</button>
          </div>
        ))}
      </div>
    </div>
  );
}

const EXPANDABLE = { problem_moves: 1, transfers_receipt: 1 };

export default function HomePage() {
  const { user, has } = useAuth();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [nd, setNd] = useState(null);
  const [sec, setSec] = useState(null);
  const [quickHits, setQuickHits] = useState([]);
  const [tasks, setTasks] = useState(null);
  const [expandedKey, setExpandedKey] = useState(null);
  const canParts = has("view_parts");
  const canManageUsers = has("manage_users");

  const reloadTasks = () => api.get("/home/tasks").then(({ data }) => setTasks(data)).catch(() => {});
  useEffect(() => { reloadTasks(); }, []);

  const handleTaskClick = (g) => {
    if (EXPANDABLE[g.key] && g.count > 0) setExpandedKey((k) => (k === g.key ? null : g.key));
    else navigate(g.link);
  };

  useEffect(() => { if (canParts) api.get("/inventory/dashboard").then(({ data }) => setD(data)).catch(() => {}); }, [canParts]);
  useEffect(() => { api.get("/notifications/dashboard").then(({ data }) => setNd(data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/home/quick-hits").then(({ data }) => setQuickHits(data.items || [])).catch(() => {}); }, []);
  useEffect(() => { if (canManageUsers) api.get("/notifications/security-activity").then(({ data }) => setSec(data)).catch(() => {}); }, [canManageUsers]);

  const STAT = { new: "text-amber-600", in_progress: "text-blue-600" };

  return (
    <div data-testid="home-page">
      {nd?.backup_unhealthy && nd?.can_manage_backups && (
        <div data-testid="dash-backup-failed" onClick={() => navigate("/backups")} className="cursor-pointer border border-[#B01E28] bg-red-50 rounded-xl p-4 mb-5 flex items-start gap-3">
          <ShieldAlert className="w-5 h-5 text-[#B01E28] shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-display font-bold text-[#B01E28]">Backup FAILED — action needed</div>
            <p className="text-sm text-slate-700 mt-0.5">A scheduled backup did not complete. Your data-loss exposure is elevated until resolved. {nd.backup_last_error ? <span className="font-mono text-xs text-slate-500">({nd.backup_last_error})</span> : null}</p>
          </div>
          <ChevronRight className="w-5 h-5 text-[#B01E28]" />
        </div>
      )}
      {nd?.can_manage_backups && nd?.backup_truth && (() => {
        const bt = nd.backup_truth;
        const box = bt.overall_severity === "red" ? "border-[#B01E28] bg-red-50"
          : bt.overall_severity === "amber" ? "border-amber-400 bg-amber-50"
          : "border-emerald-300 bg-white";
        const fmtH = (h) => h == null ? "never" : (h < 48 ? `${h}h ago` : `${Math.round(h / 24)}d ago`);
        const L = bt.landing, V = bt.verify;
        const landingText = {
          ok: `Off-site backups landing — last ${fmtH(L.hours_ago)}`,
          stale: `Off-site backups NOT landing — last ${fmtH(L.hours_ago)} (expected within ${L.threshold_hours}h)`,
          never: "No off-site backup has ever landed",
          not_configured: "Off-site backups are not configured",
        }[L.state];
        const verifyText = {
          ok: `Proven restorable — verified ${fmtH(V.hours_ago)}`,
          stale: `Not proven restorable recently — last verify ${fmtH(V.hours_ago)} (warn after ${V.threshold_days}d)`,
          failed: "Last backup verification FAILED",
          never: "Never proven restorable",
        }[V.state];
        const rowColor = (sev) => sev === "red" ? "text-[#B01E28]" : sev === "amber" ? "text-amber-700" : "text-emerald-700";
        const LandIcon = L.severity === "green" ? ShieldCheck : ShieldAlert;
        const VerIcon = V.severity === "green" ? ShieldCheck : ShieldAlert;
        return (
          <div data-testid="dash-backup-truth" data-severity={bt.overall_severity}
            onClick={() => navigate("/backups")}
            className={`cursor-pointer border ${box} rounded-xl p-4 mb-5 flex items-start gap-3`}>
            <div className="flex-1 space-y-1.5">
              <div className="font-display font-bold text-slate-800 text-sm">Backup status</div>
              <div className="flex items-center gap-2" data-testid="dash-backup-landing" data-state={L.state}>
                <LandIcon className={`w-4 h-4 shrink-0 ${rowColor(L.severity)}`} />
                <span className={`text-sm ${rowColor(L.severity)}`}>{landingText}</span>
              </div>
              <div className="flex items-center gap-2" data-testid="dash-backup-verify" data-state={V.state}>
                <VerIcon className={`w-4 h-4 shrink-0 ${rowColor(V.severity)}`} />
                <span className={`text-sm ${rowColor(V.severity)}`}>{verifyText}</span>
              </div>
              {(L.state === "stale" || L.state === "never" || V.state === "failed" || V.state === "never" || V.state === "stale") && (
                <p className="text-xs text-slate-600 pt-0.5">Landing and verifying are separate facts. Open Backups &amp; Recovery to run a backup and/or the read-only verify.</p>
              )}
            </div>
            <ChevronRight className="w-5 h-5 text-slate-400" />
          </div>
        );
      })()}
      {nd?.email_delivery && (nd.email_delivery.state !== "ok" || nd.email_delivery.warn) && (() => {
        const d = nd.email_delivery;
        const loud = d.state === "failing" || !!d.warn;  // silence must never mean "email is off"
        return (
          <div data-testid="dash-email-delivery" data-state={d.state} data-warn={d.warn ? "1" : "0"}
            onClick={() => navigate("/notifications")}
            className={`cursor-pointer border rounded-xl p-4 mb-5 flex items-start gap-3 ${
              loud ? "border-[#B01E28] bg-red-50"
                : d.state === "not_configured" ? "border-amber-400 bg-amber-50"
                : "border-slate-300 bg-slate-50"}`}>
            <MailWarning className={`w-5 h-5 shrink-0 mt-0.5 ${
              loud ? "text-[#B01E28]" : d.state === "not_configured" ? "text-amber-600" : "text-slate-400"}`} />
            <div className="flex-1">
              <div className={`font-display font-bold ${
                loud ? "text-[#B01E28]" : d.state === "not_configured" ? "text-amber-700" : "text-slate-600"}`}>
                {d.warn ? "A 'stop sending email' setting was found on this server — and IGNORED"
                  : d.state === "failing" ? "Email alerts are NOT being delivered"
                  : d.state === "not_configured" ? "Email alerts are not configured"
                  : "This environment does not send email"}
              </div>
              <p className="text-sm text-slate-700 mt-0.5">{d.line}</p>
              {d.warn && <p className="text-sm font-semibold text-[#B01E28] mt-1">{d.warn}</p>}
            </div>
            <ChevronRight className="w-5 h-5 text-slate-400" />
          </div>
        );
      })()}
      {nd?.sample_mixed_warning && (
        <div data-testid="dash-sample-mixed" onClick={() => navigate("/backups")} className="cursor-pointer border border-amber-400 bg-amber-50 rounded-xl p-4 mb-5 flex items-start gap-3">
          <FlaskConical className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-display font-bold text-amber-700">Sample data is still loaded — and real inventory has been entered</div>
            <p className="text-sm text-slate-700 mt-0.5">You're mixing demo/sample records with real data. Purge the sample data before going live so reports and counts reflect real inventory only.</p>
          </div>
          <ChevronRight className="w-5 h-5 text-amber-600" />
        </div>
      )}
      <div data-testid="home-greeting" className="flex items-center gap-2.5 mb-5 px-1">
        <div className="w-8 h-8 rounded-full bg-[#B01E28] text-white flex items-center justify-center font-display font-black text-sm shrink-0">{(user?.name || "?").slice(0, 1).toUpperCase()}</div>
        <div className="min-w-0">
          <div className="text-sm text-slate-500 leading-tight">You are signed in as</div>
          <div className="font-display font-bold text-[#1F1F1F] leading-tight truncate">{user?.name || user?.email || "—"}</div>
        </div>
      </div>

      {quickHits.length > 0 && (
        <div data-testid="home-quick-hits" className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mb-8">
          {quickHits.map((it) => (
            <QuickHit key={it.key} item={it} onClick={() => navigate(it.url)} />
          ))}
        </div>
      )}

      {tasks && tasks.groups.length > 0 && (
        <div data-testid="home-task-center" className="mb-8">
          <div className="flex items-center justify-between mb-3">
            <div className="font-mono text-[10px] uppercase tracking-widest text-slate-400 flex items-center gap-1.5"><ListChecks className="w-3.5 h-3.5" />My tasks & alerts — tap to action</div>
            <span className="font-mono text-[10px] text-slate-400" data-testid="task-total-open">{tasks.total_open} open</span>
          </div>
          {["work", "attention", "alert"].map((band) => {
            const gs = tasks.groups.filter((g) => g.band === band);
            if (!gs.length) return null;
            const expandedG = gs.find((g) => g.key === expandedKey);
            return (
              <div key={band} className="mb-4" data-testid={`task-band-${band}`}>
                <div className="text-[11px] font-bold uppercase tracking-wide text-slate-500 mb-1.5">{BAND_LABEL[band]}</div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                  {gs.map((g) => <TaskCard key={g.key} g={g} expandable={!!EXPANDABLE[g.key] && g.count > 0} expanded={expandedKey === g.key} onClick={() => handleTaskClick(g)} />)}
                </div>
                {expandedG && expandedKey === "problem_moves" && <div className="mt-3"><ProblemMovesPanel onChanged={reloadTasks} /></div>}
                {expandedG && expandedKey === "transfers_receipt" && <div className="mt-3"><TransfersReceiptPanel onReceive={() => navigate("/transfers?tab=receive")} /></div>}
              </div>
            );
          })}
        </div>
      )}

      {nd && (nd.my_open_requests?.length > 0 || nd.can_manage_requests || nd.can_cart) && (
        <div className="grid md:grid-cols-3 gap-3 mb-8">
          <div data-testid="dash-my-requests" className="md:col-span-2 border border-slate-200 bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="bg-slate-50 px-4 py-2.5 font-mono text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-100 flex items-center gap-1.5"><Inbox className="w-3.5 h-3.5" />My open requests</div>
            {(!nd.my_open_requests || nd.my_open_requests.length === 0) ? (
              <button onClick={() => navigate("/requests")} className="w-full p-5 text-center text-sm text-slate-400 hover:text-[#E0781A]">No open requests — submit one →</button>
            ) : nd.my_open_requests.map((r) => (
              <button key={r.id} data-testid={`dash-req-${r.request_number}`} onClick={() => navigate("/requests")} className="w-full flex items-center gap-2 px-4 py-2.5 border-t border-slate-100 text-sm hover:bg-slate-50 text-left">
                <span className="font-mono text-xs font-bold text-slate-500 w-24">{r.request_number}</span>
                <span className="flex-1 truncate">{r.title}</span>
                <span className={`font-mono text-[10px] uppercase ${STAT[r.status] || "text-slate-500"}`}>{(r.status || "").replace("_", " ")}</span>
                <ChevronRight className="w-4 h-4 text-slate-300" />
              </button>
            ))}
          </div>
          {(nd.can_manage_requests || nd.can_cart) && (
            <div className="grid grid-cols-2 md:grid-cols-1 gap-3">
              {nd.can_manage_requests && <Queue testid="dash-team-requests" label="Open requests" value={nd.team_open_requests ?? 0} color="text-[#B01E28]" onClick={() => navigate("/requests")} />}
              {nd.can_cart && <Queue testid="dash-carts-awaiting" label="Carts awaiting pick" value={nd.carts_awaiting_pick ?? 0} color="text-[#E0781A]" onClick={() => navigate("/carts")} />}
            </div>
          )}
        </div>
      )}

      {canManageUsers && sec && (
        <div data-testid="dash-security-activity" className="border border-slate-200 bg-white rounded-xl shadow-sm overflow-hidden mb-8 max-w-2xl">
          <div className="bg-slate-50 px-4 py-2.5 font-mono text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-100 flex items-center justify-between">
            <span className="flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-[#B01E28]" />Security activity</span>
            <button data-testid="sec-activity-settings" onClick={() => navigate("/notifications")} className="hover:text-[#E0781A] normal-case tracking-normal">configure →</button>
          </div>
          {sec.length === 0 ? (
            <div className="p-5 text-center font-mono text-xs text-slate-400">No security events yet</div>
          ) : sec.map((e, i) => (
            <button key={i} data-testid={`sec-event-${i}`} onClick={() => navigate(e.link || "/users")}
              className="w-full flex items-center gap-2 px-4 py-2.5 border-t border-slate-100 text-sm hover:bg-orange-50/50 text-left">
              <ShieldAlert className="w-3.5 h-3.5 text-[#C76714] shrink-0" />
              <span className="flex-1 truncate text-slate-700">{e.title || e.label}</span>
              <span className="font-mono text-[10px] text-slate-400 shrink-0">{(e.created_at || "").slice(0, 16).replace("T", " ")}</span>
              <ChevronRight className="w-4 h-4 text-slate-300 shrink-0" />
            </button>
          ))}
        </div>
      )}

      {canParts && d && (
        <>
          <div className="font-mono text-[10px] uppercase tracking-widest text-slate-400 mb-2 flex items-center gap-1.5"><Activity className="w-3.5 h-3.5" />Work queues — tap to open</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-8">
            <Queue testid="queue-needs-sorting" label="Needs Sorting" value={d.needs_sorting} color="text-[#B01E28]" onClick={() => navigate("/sort-queue")} />
            <Queue testid="queue-needs-id" label="Needs ID" value={d.needs_id} color="text-[#E0781A]" onClick={() => navigate("/parts?status=staging_needs_id")} />
            <Queue testid="queue-needs-price" label="Needs Price" value={d.needs_price} color="text-[#E0781A]" onClick={() => navigate("/parts?status=staging_needs_price")} />
            <Queue testid="queue-putaway-returns" label="Returns waiting for a bin" value={d.putaway_returns} color="text-[#B01E28]" onClick={() => navigate("/putaway")} />
            <Queue testid="queue-needs-putaway" label="No bin recorded" value={d.needs_putaway} color="text-[#1F1F1F]" onClick={() => navigate("/parts?dq=needs_putaway")} />
            <Queue testid="queue-available" label="Available" value={d.available} color="text-[#2E7D32]" onClick={() => navigate("/parts?status=available")} />
          </div>

          <div className="border border-slate-200 bg-white rounded-xl shadow-sm overflow-hidden max-w-2xl">
            <div className="bg-slate-50 px-4 py-2.5 font-mono text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-100">Recent activity</div>
            {(!d.recent || d.recent.length === 0) ? <div className="p-6 text-center font-mono text-xs text-slate-400">NOTHING YET — add a truck to begin</div>
              : d.recent.map((t) => (
                <div key={t.id} className="flex items-center gap-2 px-3 py-2 border-t border-slate-200 text-sm">
                  <span className="font-medium">{txLabel(t.type)}</span>
                  {t.to_status && <span className="font-mono text-[10px] text-slate-400">→ {statusLabel(t.to_status)}</span>}
                  <span className="ml-auto font-mono text-[10px] text-slate-400">{(t.timestamp || "").slice(0, 16).replace("T", " ")}</span>
                </div>
              ))}
          </div>
        </>
      )}
      {!canParts && <div className="border border-slate-200 bg-white p-6 text-sm text-slate-500">Your role focuses on tools & locations. Use the menu on the left to get around.</div>}
    </div>
  );
}
